// Debug logger utility for network and authentication issues
import { Capacitor } from '@capacitor/core';
import { logger as baseLogger } from '@/utils/logger';

const isNative = Capacitor.isNativePlatform();
const isDebug = true; // Set to false in production

// Create namespaced loggers
export const createLogger = (namespace: string) => {
  const prefix = `[${namespace}]`;
  
  return {
    log: (...args: unknown[]) => {
      if (isDebug) {
        baseLogger.log(prefix, ...args);
      }
    },
    
    info: (...args: unknown[]) => {
      if (isDebug) {
        baseLogger.info(prefix, ...args);
      }
    },
    
    warn: (...args: unknown[]) => {
      baseLogger.warn(prefix, ...args);
    },
    
    error: (...args: unknown[]) => {
      baseLogger.error(prefix, ...args);
    },
    
    // Special logger for network requests
    network: (url: string, method: string, status?: number) => {
      if (isDebug) {
        const statusText = status ? `[Status: ${status}]` : '';
        baseLogger.log(`${prefix} Network ${method} ${statusText} ${url}`);
      }
    }
  };
};

// Create default loggers
export const logger = createLogger('App');
export const authLogger = createLogger('Auth');
export const networkLogger = createLogger('Network');

// Helper to log environment information
export const logEnvironmentInfo = () => {
  logger.info('Environment:', {
    isNative,
    platform: Capacitor.getPlatform(),
    isDebug,
    userAgent: navigator.userAgent,
  });
};

// Export a function to monitor fetch requests (can be used in development)
export const monitorFetch = () => {
  if (!isDebug) return;
  
  const originalFetch = window.fetch;
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' 
      ? input 
      : input instanceof Request 
        ? input.url 
        : input.toString();
    const method = init?.method || 'GET';
    
    networkLogger.log(`Request: ${method} ${url}`);
    
    try {
      const response = await originalFetch(input, init);
      networkLogger.log(`Response: ${response.status} ${url}`);
      return response;
    } catch (error) {
      networkLogger.error(`Failed: ${url}`, error);
      throw error;
    }
  };
  
  logger.info('Fetch monitoring enabled');
};
